import { forwardRef } from "react"

import { Typography, type TypographyProps } from "@mui/material"
import type { FC } from ".."

interface WYSIWYGProps extends TypographyProps {
  content: string
}

const WYSIWYG = forwardRef<HTMLDivElement, WYSIWYGProps>(
  ({ content, variant = "body.md", sx, ...props }, ref) => {
    return (
      <Typography
        ref={ref}
        component="article"
        variant={variant}
        sx={[
          (theme) => ({
            "&": {
              "h1": {
                ...theme.typography["heading.xl"],
                mt: 20,
                mb: 15,
              },

              "h2": {
                ...theme.typography["heading.lg"],
                mt: 20,
                mb: 15,
              },

              "h3": {
                ...theme.typography["heading.md"],
                mt: 20,
                mb: 15,
              },

              "h4": {
                ...theme.typography["heading.sm"],
                mb: 5,
              },

              "> h1, > h2, > h3, > h4, > h5, > h6": {
                "&:first-of-type": {
                  mt: 0,
                },
              },

              "b, strong": {
                fontWeight: 500,
              },

              "a": {
                "color": (theme) => theme.palette["neutral.700"].main,
                "background": (theme) =>
                  `linear-gradient(to right, ${theme.palette["primary.800"].main}, ${theme.palette["primary.800"].main})`,
                "backgroundSize": "100% max(1px, 0.05em)",
                "backgroundPosition": "0 100%",
                "backgroundRepeat": "no-repeat",
                "pb": 0.5,
                "transition": "background-size 400ms",
                "textDecoration": "none",

                "&:hover": {
                  backgroundSize: "0 max(1px, 0.05em)",
                },

              },

              "p, ul, ol, dl, blockquote, pre, table, li": {
                "mb": { xs: 5, md: 7 },

                "&:last-child": {
                  mb: 0,
                },
              },

              ".small": {
                ...theme.typography["body.sm"],
              },

              ".large": {
                ...theme.typography["body.lg"],
              },

              "ul, ol": {
                "listStylePosition": "initial",
                "pl": { xs: 4, md: 5 },

                "ul, ol, p": {
                  mt: 5,
                  mb: 5,
                },

                "li": {
                  display: "list-item",
                  listStyleType: "inherit",
                  position: "relative",
                  pl: { xs: 4, md: 5 },
                  mb: 5,
                },
              },

              "ul li::marker": {
                content:
                  "url(\"data:image/svg+xml,%3Csvg width='6' height='8' viewBox='0 0 6 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.387618 0.513662L5.4353 3.82942C5.52157 3.88616 5.52157 4.11347 5.4353 4.17058L0.387618 7.48634C0.344713 7.51434 0.295842 7.4985 0.263032 7.44618L0.0335917 7.07776C9.34775e-05 7.02397 -0.00931359 6.94292 0.00972993 6.87366L0.792579 4L0.00972968 1.12634C0.00330536 1.10276 9.32381e-05 1.07771 9.3237e-05 1.05266C9.32349e-05 1.00476 0.0117947 0.957606 0.0335915 0.922238L0.263031 0.55382C0.295612 0.501504 0.344712 0.485663 0.387618 0.513662Z' fill='%23F05B2A'/%3E%3C/svg%3E%0A\");",
              },

              "ol": {
                "listStyleType": "decimal",
                "pl": { xs: 6.5, md: 7.5 },

                "& > li": {
                  pl: { xs: 1.5, md: 2.5 },
                },
              },

              "ol ol": {
                listStyleType: "lower-alpha",
              },

              "ol ol ol": {
                listStyleType: "lower-roman",
              },

              "ol ol ol ol": {
                listStyleType: "lower-greek",
              },

              "ol ol ol ol ol": {
                listStyleType: "decimal",
              },

              "ol ol ol ol ol ol": {
                listStyleType: "lower-alpha",
              },

              ".content-table": {
                overflowX: "auto",
              },

              "table": {
                width: "100%",
                borderCollapse: "collapse",
              },

              "td, th": {
                ...theme.typography["body.md"],
                textAlign: "inherit",
                overflow: "hidden",
                whiteSpace: "nowrap",
                height: theme.spacing(20),
                padding: 4,
                border: `1px solid ${theme.palette["neutral.200"].main}`,

                [theme.breakpoints.down("md")]: {
                  fontSize: theme.spacing(3),
                  lineHeight: 1.8,
                },
              },

              "th": {
                color: theme.palette["secondary.800"].main,
                background: "#DAE7F1",
              },

              "td": {
                color: theme.palette["neutral.700"].main,
                lineHeight: theme.typography["body.md"].lineHeight,
              },

              ".text-align-center": {
                textAlign: "center",
              },

              "tr:nth-child(even) td": {
                background: "#ffffff", 
              },

              "tr:nth-child(odd) td": {
                background: "#f8f8f8", 
              },

            },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }
)
WYSIWYG.displayName = "WYSIWYG"

export default WYSIWYG
